import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { epochToString } from "../../../../app/common";
import { dateTimeFormat } from "../../../helpers/formatDateTime";
import { HoverPopper } from "../hoverPopper/HoverPopper";
import HtmlSnippet from "../HtmlSnippet";

export type RejectionMessagePreviewData = {
  firstName?: string;
  lastName?: string;
  at?: EpochTimeStamp;
  message?: string;
};

type Props = {
  enabled: boolean;
  cacheKey?: string;
  fetchMessageDetails: () => Promise<RejectionMessagePreviewData>;
};

export const RejectionMessagePreview = ({
  enabled,
  cacheKey,
  fetchMessageDetails,
}: Props) => {
  const [rejectedMessage, setRejectedMessage] =
    useState<RejectionMessagePreviewData | null>(null);
  const [isLoadingRejectedMessage, setIsLoadingRejectedMessage] =
    useState(false);
  const [hasFetchedRejectedMessage, setHasFetchedRejectedMessage] =
    useState(false);

  useEffect(() => {
    setRejectedMessage(null);
    setIsLoadingRejectedMessage(false);
    setHasFetchedRejectedMessage(false);
  }, [cacheKey]);

  const fetchRejectedMessage = useCallback(() => {
    if (!enabled || hasFetchedRejectedMessage || isLoadingRejectedMessage) {
      return;
    }

    setIsLoadingRejectedMessage(true);
    fetchMessageDetails()
      .then((data) => {
        if (data.message) {
          setRejectedMessage(data);
        }
      })
      .catch(() => {
        setRejectedMessage(null);
      })
      .finally(() => {
        setHasFetchedRejectedMessage(true);
        setIsLoadingRejectedMessage(false);
      });
  }, [
    enabled,
    fetchMessageDetails,
    hasFetchedRejectedMessage,
    isLoadingRejectedMessage,
  ]);

  if (!enabled) {
    return null;
  }

  return (
    <HoverPopper
      placement="top-start"
      fetchData={fetchRejectedMessage}
      maxWidth={420}
      trigger={
        <IconButton
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <EmailOutlinedIcon fontSize="small" />
        </IconButton>
      }
    >
      <Box
        sx={{
          p: 1.5,
          maxWidth: 420,
          maxHeight: "70vh",
          overflowY: "auto",
          whiteSpace: "normal",
        }}
      >
        {isLoadingRejectedMessage || !hasFetchedRejectedMessage ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 56,
            }}
          >
            <CircularProgress size={18} />
          </Box>
        ) : (
          <>
            {rejectedMessage?.firstName && rejectedMessage?.lastName && (
              <Typography variant="caption" color="text.secondary">
                {`${rejectedMessage.firstName} ${rejectedMessage.lastName}${
                  rejectedMessage.at
                    ? `, ${epochToString(dateTimeFormat, rejectedMessage.at)}`
                    : ""
                }`}
              </Typography>
            )}
            {rejectedMessage?.message ? (
              <Box
                sx={{
                  mt: rejectedMessage?.firstName ? 0.5 : 0,
                  "& .ql-editor": {
                    p: 0,
                    lineHeight: 1.4,
                    fontSize: "0.875rem",
                  },
                }}
              >
                <div className="ql-editor">
                  <HtmlSnippet html={rejectedMessage.message} />
                </div>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ mt: rejectedMessage?.firstName ? 0.5 : 0 }}
              >
                -
              </Typography>
            )}
          </>
        )}
      </Box>
    </HoverPopper>
  );
};
